import { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import usePrevious from 'common/usePrevious';
import _get from 'lodash/get';
import _some from 'lodash/some';
import _isEmpty from 'lodash/isEmpty';
import {
  bulkUploadDeliveriesSelector,
  bulkUploadGroupingClusterMappingsSelector,
  bulkUploadGroupingFetchingQuoteSelector,
  bulkUploadGroupingIsGroupedSelector,
  bulkUploadGroupingSelector,
  bulkUploadGroupingTrialDeliveriesSelector,
} from 'selectors/new-bulk-upload-selectors';
import NewBulkUploadDuck from 'ducks/new-bulk-upload-duck';
import {
  filterSameDelivery,
  calculateGroupingOptimizedAmount,
} from 'helper/new-bulk-booking';

import pickUpKey from '../common/pick-up-key';

export const useProceedToPayment = (
  setIsShowRouteOptimizationModal,
  stepForwardFunc,
  totalAmount,
  optimizedTotalAmount,
  dontShowGroupingOptimizeModal,
  isEligibleForGrouping,
  eligibleGroupingDeliveryIDs,
  enableMSD,
) => {
  const bulkUploadFetchingGroupQuote = useSelector(
    bulkUploadGroupingFetchingQuoteSelector,
  );

  const bulkUploadGrouping = useSelector(bulkUploadGroupingSelector);

  const [callbackPaymentClick, setCallbackPaymentClick] = useState(false);

  const dispatch = useDispatch();

  const previousBulkUploadFetchingGroupQuote = usePrevious(
    bulkUploadFetchingGroupQuote,
  );

  const bulkUploadGroupingTriggeredGrouping = _get(
    bulkUploadGrouping,
    'triggeredGrouping',
  );

  // / When silently fetch trialGrouping in background
  useEffect(
    () => {
      if (
        bulkUploadGroupingTriggeredGrouping &&
        callbackPaymentClick &&
        !bulkUploadFetchingGroupQuote &&
        previousBulkUploadFetchingGroupQuote
      ) {
        setCallbackPaymentClick(false);

        if (
          !dontShowGroupingOptimizeModal &&
          optimizedTotalAmount <= totalAmount && // / Maybe not change in price, but can be grouping to MSD
          (optimizedTotalAmount !== 0 || // / Handle case cannot get clustering result - non-200
            (optimizedTotalAmount === 0 && totalAmount === 0)) // / Jaya case support
        ) {
          setIsShowRouteOptimizationModal(true);
        } else {
          stepForwardFunc();
        }
      }
    },
    [
      bulkUploadGroupingTriggeredGrouping,
      callbackPaymentClick,
      totalAmount,
      optimizedTotalAmount,
      setCallbackPaymentClick,
      stepForwardFunc,
      bulkUploadFetchingGroupQuote,
      previousBulkUploadFetchingGroupQuote,
      setIsShowRouteOptimizationModal,
    ],
  );

  // / When user click on the Go To Payment button
  const onProceedToPayment = useCallback(
    () => {
      if (
        dontShowGroupingOptimizeModal ||
        bulkUploadGroupingTriggeredGrouping
      ) {
        stepForwardFunc();
        return;
      }

      // Check if enableMSD feature flag is enabled before triggering clustering
      if (enableMSD && isEligibleForGrouping) {
        dispatch(
          NewBulkUploadDuck.creators.groupingTriggerClusteringDeliveries(
            eligibleGroupingDeliveryIDs,
          ),
        );
        setCallbackPaymentClick(true);
        return;
      }
      // If enableMSD is false or not eligible for grouping, proceed directly to payment
      stepForwardFunc();
    },
    [
      stepForwardFunc,
      enableMSD,
      isEligibleForGrouping,
      eligibleGroupingDeliveryIDs,
      dontShowGroupingOptimizeModal,
      bulkUploadGroupingTriggeredGrouping,
      setCallbackPaymentClick,
      dispatch,
    ],
  );

  return { callbackPaymentClick, setCallbackPaymentClick, onProceedToPayment };
};

export const useGroupSwitcher = () => {
  const dispatch = useDispatch();
  const bulkUploadFetchingGroupQuote = useSelector(
    bulkUploadGroupingFetchingQuoteSelector,
  );
  const bulkUploadGroupingIsGrouped = useSelector(
    bulkUploadGroupingIsGroupedSelector,
  );
  const [callbackGroupSwitcher, setCallbackGroupSwitcher] = useState(false);

  const previousBulkUploadFetchingGroupQuote = usePrevious(
    bulkUploadFetchingGroupQuote,
  );

  useEffect(
    () => {
      if (
        !bulkUploadFetchingGroupQuote &&
        previousBulkUploadFetchingGroupQuote
      ) {
        if (callbackGroupSwitcher) {
          dispatch(
            NewBulkUploadDuck.creators.groupingSwapDeliveries(
              !bulkUploadGroupingIsGrouped,
            ),
          );
          setCallbackGroupSwitcher(false);
        }
      }
    },
    [
      previousBulkUploadFetchingGroupQuote,
      bulkUploadFetchingGroupQuote,
      callbackGroupSwitcher,
      bulkUploadGroupingIsGrouped,
    ],
  );

  return setCallbackGroupSwitcher;
};

export const usePickupDeliveries = (deliveries, currentPickUpKey) => {
  const pickUps = useMemo(
    () => {
      const uniquePickUps = [];
      deliveries.forEach(delivery => {
        const pickUp = _get(delivery, ['detail', 'pickUp'], null);
        if (pickUp) {
          const key = pickUpKey(pickUp);
          const uniquePickUp = uniquePickUps.find(
            pickup => pickup.pickUpKey === key,
          );
          if (!uniquePickUp) {
            uniquePickUps.push({
              pickUpKey: key,
              deliveryIDs: [_get(delivery, 'id')],
            });
          } else {
            uniquePickUp.deliveryIDs.push(_get(delivery, 'id'));
          }
        }
      });
      return uniquePickUps;
    },
    [deliveries],
  );

  const filteredDeliveries = useMemo(
    () => {
      if (!currentPickUpKey) {
        return [];
      }
      const currentPickup = pickUps.find(
        pickUp => pickUp.pickUpKey === currentPickUpKey,
      );
      if (!currentPickup) {
        return [];
      }
      return deliveries.filter(delivery =>
        currentPickup.deliveryIDs.includes(delivery.id),
      );
    },
    [currentPickUpKey, pickUps, deliveries],
  );

  return { pickUps, filteredDeliveries };
};

export const useEligibleDeliveries = (deliveries, pickUps) => {
  const eligibleGroupingDeliveryIDs = useMemo(
    () => {
      const deliveryIDGroups = [];
      pickUps.forEach(pickUp => {
        const deliveryIDs = _get(pickUp, 'deliveryIDs', []);
        const groups = filterSameDelivery(deliveries, deliveryIDs);
        if (!_isEmpty(groups)) {
          deliveryIDGroups.push(...groups);
        }
      });
      return deliveryIDGroups;
    },
    [deliveries, pickUps],
  );

  const isEligibleForGrouping = useMemo(
    () => _some(eligibleGroupingDeliveryIDs, group => !_isEmpty(group)),
    [eligibleGroupingDeliveryIDs],
  );

  return { eligibleGroupingDeliveryIDs, isEligibleForGrouping };
};

export const useOptmizeAmount = () => {
  const deliveries = useSelector(bulkUploadDeliveriesSelector);
  const bulkUploadGroupingTrialDeliveries = useSelector(
    bulkUploadGroupingTrialDeliveriesSelector,
  );

  const clusterMappings = useSelector(
    bulkUploadGroupingClusterMappingsSelector,
  );

  return useMemo(
    () => {
      const {
        originalAmount,
        optimisedAmount,
      } = calculateGroupingOptimizedAmount({
        allDeliveries: deliveries,
        trialDeliveries: bulkUploadGroupingTrialDeliveries,
        clusterMappings,
      });
      return {
        totalAmount: originalAmount,
        optimizedTotalAmount: optimisedAmount,
      };
    },
    [bulkUploadGroupingTrialDeliveries, deliveries, clusterMappings],
  );
};

export default {};
